/**
 * Environment Configuration
 *
 * NEXT_PUBLIC_* values are inlined at `next build` (see Dockerfile ARG/ENV).
 * Changing them in runtime K8s env alone does not update the client bundle —
 * rebuild the image with matching build-args.
 *
 * Public prod (mechagora.com): set NEXT_PUBLIC_WS_BASE_URL and
 * NEXT_PUBLIC_API_BASE_URL to mechagora.com only (e.g. wss://api.mechagora.com/ws).
 * Sandbox edge image defaults bake api.sandbox.mechagora.net / sandbox.mechagora.net.
 * Fallback WS below is for local development only.
 */

export const ENV_CONFIG = {
  // Same-origin portal BFF preferred for sessionToken cookies: leave empty so browser hits /api/* on the portal host.
  // Do not set this to api.sandbox.mechagora.net for portal cookie flows — that host is QTP.
  // Server-side QTP proxy uses BACKEND_URL / QTP_INTERNAL_URL (see app/api/lib/backend-urls.ts).
  API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL || '',
  // QTP BFF OIDC cookie path (__Host-app_session on api.*). Browser calls this host with credentials.
  // Login: {QTP_BFF_URL}/auth/start — see domains/auth/services/bff-auth.ts
  QTP_BFF_URL:
    process.env.NEXT_PUBLIC_QTP_BFF_URL ||
    process.env.NEXT_PUBLIC_QTP_AUTH_URL ||
    'https://api.sandbox.mechagora.net',
  // When true, client login may redirect to QTP /auth/start instead of portal Zitadel login.
  USE_QTP_BFF_AUTH: process.env.NEXT_PUBLIC_USE_QTP_BFF_AUTH === 'true',
  // WS: set NEXT_PUBLIC_WS_BASE_URL in deploy (e.g. wss://api.mechagora.com).
  // Path `/ws/{channel}?ticket=` is appended by secureSocket (ticket-based SOC2 flow).
  // Sandbox default matches edge gateway host; override for local/dev as needed.
  WS_BASE_URL:
    process.env.NEXT_PUBLIC_WS_BASE_URL ||
    process.env.NEXT_PUBLIC_WS_URL ||
    'wss://api.sandbox.mechagora.net',
  API_TIMEOUT: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '30000'),
  API_RETRY_ATTEMPTS: parseInt(process.env.NEXT_PUBLIC_API_RETRY_ATTEMPTS || '3'),
  API_RETRY_DELAY: parseInt(process.env.NEXT_PUBLIC_API_RETRY_DELAY || '1000'),

  NODE_ENV: process.env.NODE_ENV || 'development',
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',

  ENABLE_DEBUG_LOGGING: process.env.NEXT_PUBLIC_ENABLE_DEBUG_LOGGING === 'true',
  ENABLE_AUTO_REFRESH: process.env.NEXT_PUBLIC_ENABLE_AUTO_REFRESH !== 'false',

  // Development credentials - Use environment variables only
  DEFAULT_USERNAME: process.env.NEXT_PUBLIC_DEFAULT_USERNAME || '',
  DEFAULT_PASSWORD: process.env.NEXT_PUBLIC_DEFAULT_PASSWORD || '',

  get API_ENDPOINTS() {
    const base = this.API_BASE_URL || '';
    return {
      health: `${base}/health`,
      root: `${base}/`,
      // Authentication endpoints (Next.js API routes)
      auth: {
        register: `${base}/api/auth/register`,
        login: `${base}/api/auth/login`,
        logout: `${base}/api/auth/logout`,
profile: `${base}/api/auth/profile`,
        mfaVerify: `${base}/api/auth/mfa/verify`,
        mfaSetup: `${base}/api/auth/mfa/setup`,
        changePassword: `${base}/api/auth/change-password`,
        health: `${base}/api/auth/health`,
      },
      // Trading endpoints (same-origin BFF: /api/v1/trading/* → QTP)
      trading: {
        account: `${base}/api/v1/trading/account`,
        orders: `${base}/api/v1/trading/orders`,
        positions: `${base}/api/v1/trading/positions`,
        portfolio: `${base}/api/v1/trading/portfolio`,
        analytics: `${base}/api/v1/trading/analytics`,
        performance: `${base}/api/v1/trading/performance`,
        rebalancing: `${base}/api/v1/trading/rebalancing`,
      },
      // AI Models endpoints
      aiModels: {
        models: `${base}/ai-models/models`, // Backend uses /ai-models/
        training: `${base}/api/v1/ai-models/training`,
        predictions: `${base}/api/v1/ai-models/predictions`,
        features: `${base}/api/v1/ai-models/features`,
      },
      // Market Data endpoints (matches backend structure)
      marketData: {
        ohlcv: `${base}/market-data`, // Backend uses /market-data/{symbol}/ohlcv
        ticker: `${base}/market-data`, // Backend uses /market-data/{symbol}/ticker
        sources: `${base}/market-data/sources`,
        symbols: `${base}/market-data/symbols`,
        indicators: `${base}/market-data`, // Backend uses /market-data/{symbol}/indicators
        analysis: `${base}/market-data`, // Backend uses /market-data/{symbol}/analysis
        historical: `${base}/market-data`, // Backend uses /market-data/{symbol}/historical
      },
      // Risk Management endpoints
      riskManagement: {
        assessment: `${base}/risk/limits`, // Backend uses /risk/limits
        limits: `${base}/risk/limits`,
        positions: `${base}/risk/positions`,
        monitoring: `${base}/risk/monitoring`,
        reporting: `${base}/risk/reporting`,
        alerts: `${base}/risk/alerts`,
        violations: `${base}/risk/violations`,
      },
      // Portfolio endpoints
      portfolio: {
        overview: `${base}/api/v1/portfolio/overview`,
        positions: `${base}/api/v1/portfolio/positions`,
        performance: `${base}/api/v1/portfolio/performance`,
        allocation: `${base}/api/v1/portfolio/allocation`,
        history: `${base}/api/v1/portfolio/history`,
        trades: `${base}/api/v1/portfolio/trades`,
        analysis: `${base}/api/v1/portfolio/analysis`,
      },
      // Execution endpoints
      execution: {
        orders: `${base}/api/v1/execution/orders`,
        trades: `${base}/api/v1/execution/trades`,
        status: `${base}/api/v1/execution/status`,
      },
      // Data Quality endpoints
      dataQuality: {
        validate: `${base}/data-quality/validate`,
        metrics: `${base}/data-quality/metrics`,
        reports: `${base}/data-quality/reports`,
      },
    };
  },

  get WS_ENDPOINTS() {
    // Normalize so we never double `/ws` when env already includes it
    let wsBase = (this.WS_BASE_URL || 'wss://api.sandbox.mechagora.net').replace(
      /\/+$/,
      ''
    );
    if (wsBase.endsWith('/ws')) {
      wsBase = wsBase.slice(0, -3);
    }
    return {
      /** Prefer secureSocket channel + ticket over these static URLs */
      executions: `${wsBase}/ws/executions`,
      marketData: `${wsBase}/ws/market-data`,
      orders: `${wsBase}/ws/orders`,
      notifications: `${wsBase}/ws/notifications`,
      /** Chat (LLM) – use when backend exposes /ws/chat with auth */
      chat: process.env.NEXT_PUBLIC_WS_CHAT_URL || `${wsBase}/ws/chat`,
    };
  },

  /** Chat LLM streaming API (server-side: CHAT_LLM_STREAM_URL / CHAT_LLM_URL) */
  CHAT_STREAM_API_PATH: '/api/chat/stream',
} as const;

export default ENV_CONFIG;


